import type { MetadataItem } from '../tool-config-types';
import { LiveDot } from '../LiveDot';

type StreamingOutput = {
  mode?: string;
  sessionId?: string;
  url?: string;
  status?: string;
  count?: number;
  messages?: unknown[];
  isActive?: boolean;
};

type MetadataExtractorContext = {
  input: unknown;
  output?: unknown;
  status?: string;
};

const isStreamingOutput = (output: unknown): output is StreamingOutput =>
  typeof output === 'object' && output !== null;

const getMessageCount = (output: StreamingOutput): number =>
  output.count ?? output.messages?.length ?? 0;

export const extractSseClientMetadata = ({
  output,
}: MetadataExtractorContext): MetadataItem[] => {
  if (!isStreamingOutput(output)) return [];

  if (output.mode === 'connected' || output.status === 'active') {
    return [{ value: 'Listening', icon: LiveDot() }];
  }

  const count = getMessageCount(output);
  if (output.mode === 'stopped' || output.isActive === false) {
    return [{ value: `${count} ${count === 1 ? 'event' : 'events'}` }];
  }

  if (output.mode === 'poll' || output.messages !== undefined) {
    return [
      {
        value: `${count} ${count === 1 ? 'event' : 'events'}`,
        color: count > 0 ? 'green' : undefined,
      },
    ];
  }

  return [];
};

export const extractWebSocketClientMetadata = ({
  output,
}: MetadataExtractorContext): MetadataItem[] => {
  if (!isStreamingOutput(output)) return [];

  if (output.mode === 'connected' || output.status === 'active') {
    return [{ value: 'Connected', icon: LiveDot() }];
  }

  if (output.mode === 'sent') {
    return [{ value: 'Sent' }];
  }

  if (output.mode === 'queued') {
    return [{ value: 'Queued' }];
  }

  const count = getMessageCount(output);
  if (output.mode === 'stopped' || output.isActive === false) {
    return [{ value: `${count} ${count === 1 ? 'msg' : 'msgs'}` }];
  }

  if (output.mode === 'poll' || output.messages !== undefined) {
    return [
      {
        value: `${count} ${count === 1 ? 'msg' : 'msgs'}`,
        color: count > 0 ? 'green' : undefined,
      },
    ];
  }

  return [];
};
